import React, { memo, useEffect, useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import localforage from "localforage";
import {
  IconRefresh,
  IconChartBar,
  IconUser,
  IconUsers,
  IconBriefcase,
  IconMessage,
  IconTag,
  IconSpeakerphone,
  IconKey,
  IconAlertTriangle,
  IconSettings,
  IconBuilding,
  IconApps,
  IconActivity,
  IconLogout,
  IconChevronsLeft,
  IconChevronsRight,
  IconChevronDown,
  IconSun,
  IconMoon,
} from "@tabler/icons-react";
import { AppConstant, ImageConstant, KeyConstant, LangConstant, PathConstant } from "const";
import { AdvancedConfirmDialog } from "components";
import { AuthUtils, EventBusUtils, StorageUtils } from "utils";
import { useRole } from "hooks";


const Sidebar = ({ isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const history = useHistory();

  const { t: getLabel } = useTranslation(LangConstant.NS_COMMON);

  const sidebarTitles = getLabel(LangConstant.OBJ_SIDEBAR_TITLE, { returnObjects: true });

  const { isServiceAdmin } = useRole();

  const [isConfirmationDialog, setIsConfirmationDialog] = useState(false);
  const [branchAvatar, setBranchAvatar] = useState("");
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);
  const [expandedMenu, setExpandedMenu] = useState(() => {
    // Auto-expand group whose child is currently active
    const items = [
      {
        children: [
          { href: PathConstant.COMPANY_MANAGEMENT },
          { href: PathConstant.DEPARTMENT_MANAGEMENT },
          { href: PathConstant.POSITION_MANAGEMENT },
        ],
      },
    ];
    for (let i = 0; i < items.length; i++) {
      if (items[i].children && items[i].children.some(c => window.location.pathname === c.href)) {
        return i;
      }
    }
    return null;
  });

  const [themeMode, setThemeMode] = useState(() => {
    const stored = localStorage.getItem("tabler_theme") || "system";
    if (stored === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return stored;
  });
  const isDarkMode = themeMode === "dark";

  useEffect(() => {
    const handleThemeChange = (newTheme) => {
      setThemeMode(newTheme);
    };
    EventBusUtils.on("changeTheme", handleThemeChange);
    return () => EventBusUtils.off("changeTheme", handleThemeChange);
  }, []);

  const handleCloseConfirmationDialog = () => {
    setIsConfirmationDialog(false);
  };

  const sidebarItems = [
    {
      href: PathConstant.STATISTIC,
      icon: IconChartBar,
      title: sidebarTitles.statistic,
    },
    {
      icon: IconBuilding,
      title: sidebarTitles.companyManagement,
      children: [
        {
          href: PathConstant.COMPANY_MANAGEMENT,
          title: sidebarTitles.companyProfile,
        },
        {
          href: PathConstant.DEPARTMENT_MANAGEMENT,
          title: sidebarTitles.departmentManagement,
        },
        {
          href: PathConstant.POSITION_MANAGEMENT,
          title: sidebarTitles.positionManagement,
        },
      ],
    },
    {
      href: PathConstant.USER_MANAGEMENT,
      icon: IconUsers,
      title: sidebarTitles.userManagement,
    },
    {
      href: PathConstant.MEDIA_MANAGEMENT,
      icon: IconMessage,
      title: sidebarTitles.mediaManagement,
    },
    {
      href: PathConstant.CHANNEL_MANAGEMENT,
      icon: IconSpeakerphone,
      title: sidebarTitles.channelManagement,
    },
    {
      href: PathConstant.DEVICE_MANAGEMENT,
      icon: ImageConstant.DeviceManagementIcon,
      title: sidebarTitles.deviceManagement,
    },
    {
      href: PathConstant.LICENSE,
      icon: IconKey,
      title: sidebarTitles.license,
    },
    {
      href: PathConstant.SYSTEM_TRACKING,
      icon: IconAlertTriangle,
      title: sidebarTitles.systemTracking,
    },
    {
      href: PathConstant.SETTINGS,
      icon: IconSettings,
      title: sidebarTitles.settings,
    },
    {
      href: PathConstant.CONFERENCE_MANAGEMENT,
      icon: IconApps,
      title: sidebarTitles.conferenceManagement,
    },
    {
      href: PathConstant.SERVER_MONITORING,
      icon: IconActivity,
      title: sidebarTitles.serverMonitoring,
    },
  ];

  const filteredSidebarItems = isServiceAdmin
    ? sidebarItems.filter(item => {
        if (item.children) {
          return item.children.some(child =>
            [
              PathConstant.COMPANY_MANAGEMENT,
              PathConstant.DEPARTMENT_MANAGEMENT,
              PathConstant.POSITION_MANAGEMENT,
            ].includes(child.href),
          );
        }
        return [
          PathConstant.USER_MANAGEMENT,
          PathConstant.SETTINGS,
          PathConstant.CONFERENCE_MANAGEMENT,
        ].includes(item.href);
      })
    : sidebarItems;

  const redirect = href => {
    setIsMobileOpen(false);
    if (href) history.push(href);
  };

  const handleGetBranchAvatar = avatar => {
    setBranchAvatar(avatar);
  };

  useEffect(() => {
    EventBusUtils.on(AppConstant.EVENT_BUS_EVENTS.fetchBranchDetails, handleGetBranchAvatar);
    return () => EventBusUtils.off(AppConstant.EVENT_BUS_EVENTS.fetchBranchDetails, handleGetBranchAvatar);
  });

  useEffect(() => {
    const branchAttachmentId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ATTACHMENT);
    localforage.getItem(KeyConstant.KEY_CACHED_BRANCH_AVATAR_BLOB + branchAttachmentId).then(avatar => {
      if (avatar) setBranchAvatar(URL.createObjectURL(avatar));
    });
  }, []);

  return (
    <aside className="navbar navbar-vertical navbar-expand-lg navbar-dark bg-dark border-end">
      <style>{`
        body {
          overflow-y: auto !important;
        }

        /* --- LAYOUT CHUNG CHO SIDEBAR LUÔN TỐI (DARK SIDEBAR) --- */
        .navbar-vertical.navbar-dark {
          background-color: #182433 !important; /* Màu tối sẫm đặc trưng của Tabler */
          border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
        }

        /* Override border-secondary-subtle trong sidebar để luôn mỏng và tối (sidebar luôn dark) */
        .navbar-vertical.navbar-dark .border-secondary-subtle {
          border-color: rgba(255, 255, 255, 0.08) !important;
        }
        .navbar-vertical.navbar-dark .border-bottom.border-secondary-subtle {
          border-bottom-width: 1px !important;
          border-bottom-style: solid !important;
          border-bottom-color: rgba(255, 255, 255, 0.08) !important;
        }
        .navbar-vertical.navbar-dark .border-top.border-secondary-subtle {
          border-top-width: 1px !important;
          border-top-style: solid !important;
          border-top-color: rgba(255, 255, 255, 0.08) !important;
        }

        /* Font chữ và kích thước menu item theo Tabler chuẩn */
        .navbar-vertical .navbar-nav .nav-link {
          font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, sans-serif !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          color: #94a3b8 !important; /* Màu xám nhạt Tabler */
          padding: 0.5rem 0.75rem !important;
          margin: 0.125rem 0.5rem !important;
          border-radius: 6px !important;
          transition: all 0.2s ease-in-out !important;
        }

        .navbar-vertical .navbar-nav .nav-link .nav-link-icon {
          color: #94a3b8 !important;
          margin-right: 0.5rem !important;
          transition: all 0.2s ease-in-out !important;
        }

        /* Hover style */
        .navbar-vertical .navbar-nav .nav-link:hover {
          color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.06) !important;
        }
        .navbar-vertical .navbar-nav .nav-link:hover .nav-link-icon {
          color: #ffffff !important;
        }

        /* Active style (Trang hiện tại) */
        .navbar-vertical .navbar-nav .nav-item.active {
          position: relative !important;
        }
        
        /* Vạch đứng màu xanh dương bên cạnh trái của active item giống Tabler gốc */
        .navbar-vertical .navbar-nav .nav-item.active::after {
          content: "" !important;
          position: absolute !important;
          left: 0 !important;
          top: 6px !important;
          bottom: 6px !important;
          width: 3px !important;
          background-color: #206bc4 !important; /* Xanh dương Tabler */
          border-radius: 0 4px 4px 0 !important;
        }

        .navbar-vertical .navbar-nav .nav-item.active .nav-link {
          color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.08) !important;
          font-weight: 600 !important;
        }
        .navbar-vertical .navbar-nav .nav-item.active .nav-link .nav-link-icon {
          color: #ffffff !important;
        }

        /* Logout button màu đỏ đặc thù */
        .navbar-vertical .navbar-nav .nav-link.text-danger {
          color: #ef4444 !important;
        }
        .navbar-vertical .navbar-nav .nav-link.text-danger .nav-link-icon {
          color: #ef4444 !important;
        }
        .navbar-vertical .navbar-nav .nav-link.text-danger:hover {
          background-color: rgba(239, 68, 68, 0.1) !important;
          color: #f87171 !important;
        }
        .navbar-vertical .navbar-nav .nav-link.text-danger:hover .nav-link-icon {
          color: #f87171 !important;
        }

        /* --- LAYOUT TRÊN DESKTOP (>= 992px) --- */
        @media (min-width: 992px) {
          .navbar-vertical {
            position: fixed !important;
            top: 0 !important;
            bottom: 0 !important;
            left: 0 !important;
            width: 250px !important;
            transition: width 0.25s ease-in-out !important;
            z-index: 1030 !important;
          }
          .navbar-vertical-collapsed .navbar-vertical {
            width: 80px !important;
          }
          
          .page-wrapper {
            margin-left: 250px !important;
            transition: margin-left 0.25s ease-in-out !important;
          }
          .navbar-vertical-collapsed .page-wrapper {
            margin-left: 80px !important;
          }
        }

        /* --- LAYOUT TRÊN MOBILE (< 992px) --- */
        @media (max-width: 991.98px) {
          body {
            overflow-x: hidden !important;
          }
          .page-wrapper {
            margin-left: 0 !important;
            padding-top: 56px !important;
          }
          
          /* Biến đổi Sidebar dọc thành Header ngang trên mobile */
          .navbar-vertical {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            height: 56px !important;
            width: 100% !important;
            z-index: 1040 !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.15) !important;
          }
          
          /* Căn ngang Header Logo và Hamburger */
          .navbar-vertical .container-fluid {
            flex-direction: row !important;
            justify-content: space-between !important;
            align-items: center !important;
            height: 100% !important;
            padding: 0 1rem !important;
          }
          
          /* Căn nhỏ Logo */
          .navbar-vertical .navbar-brand {
            padding: 0.25rem 0.5rem !important;
            width: auto !important;
            border-bottom: none !important;
          }
          .navbar-vertical .navbar-brand img {
            width: 32px !important;
            height: 32px !important;
          }
          .navbar-vertical .navbar-brand span {
            font-size: 1.1rem !important;
          }

          /* Toggler Button */
          .navbar-vertical .navbar-toggler {
            margin: 0 !important;
            align-self: center !important;
            padding: 4px 8px !important;
            border: 1px solid rgba(255, 255, 255, 0.15) !important;
          }
          
          /* Panel Menu xổ xuống trên mobile */
          .navbar-vertical .navbar-collapse {
            background-color: #182433 !important; /* Luôn là màu tối sẫm */
            padding: 1rem 1.5rem !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3) !important;
            z-index: 1050 !important;
            position: absolute !important;
            top: 56px !important;
            left: 0 !important;
            right: 0 !important;
            max-height: calc(100vh - 56px) !important;
            overflow-y: auto !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
          }
          .navbar-vertical .navbar-collapse.show {
            display: block !important;
          }
        }
      `}</style>
      <div className="container-fluid d-flex flex-column h-100 p-0 position-relative">
        {/* Desktop Collapse Toggle Button */}
        <button
          onClick={onToggleCollapse}
          className="btn btn-icon d-none d-lg-inline-flex position-absolute"
          style={{
            width: "16px",
            height: "28px",
            right: "-8px",
            top: "50px",
            zIndex: 10,
            borderRadius: "4px",
            backgroundColor: "#2a3a4e",
            border: "1px solid rgba(255,255,255,0.12)",
            padding: 0,
            color: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isCollapsed ? <IconChevronsRight size={14} /> : <IconChevronsLeft size={14} />}
        </button>

        {/* Mobile Toggle Button */}
        <button
          className="navbar-toggler ms-2 mt-2 align-self-start"
          type="button"
          onClick={() => setIsMobileOpen(prev => !prev)}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Brand / Logo */}
        <div className="navbar-brand navbar-brand-autodark px-3 py-3 d-flex align-items-center justify-content-center w-100 border-bottom border-secondary-subtle">
          <a href="#" className="d-flex flex-row flex-lg-column align-items-center text-decoration-none gap-2" onClick={(e) => { e.preventDefault(); redirect(PathConstant.ROOT); }}>
            <img
              src={branchAvatar || window.env.URL_DEFAULT_AVATAR || ImageConstant.TriosLogo}
              style={{
                width: isCollapsed ? "32px" : "40px",
                height: isCollapsed ? "32px" : "40px",
                objectFit: "contain",
                borderRadius: !isServiceAdmin ? "50%" : "0%",
                transition: "all 0.2s",
              }}
              alt="Logo"
            />
            {!isCollapsed && (
              <span className="text-white text-center" style={{ fontSize: "12px", lineHeight: "1.3", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_NAME) || ""}
              </span>
            )}
          </a>
        </div>

        {/* Navigation Menus */}
        <div className={`collapse navbar-collapse w-100 flex-grow-1 overflow-auto ${isMobileOpen ? "show" : ""}`} id="sidebar-menu">
          <ul className="navbar-nav pt-lg-3 px-2 d-flex flex-column gap-1">
            {filteredSidebarItems.map((sidebar, index) => {
              // Group with children (collapsible submenu)
              if (sidebar.children) {
                const Icon = sidebar.icon;
                const isGroupActive = sidebar.children.some(child => location.pathname === child.href);
                const isExpanded = expandedMenu === index;

                return (
                  <li key={index} className="nav-item">
                    <a
                      className={`nav-link d-flex align-items-center ${isGroupActive && !isExpanded ? "active" : ""}`}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setExpandedMenu(prev => prev === index ? null : index);
                      }}
                    >
                      <span className="nav-link-icon d-inline-flex align-items-center justify-content-center">
                        <Icon size={18} />
                      </span>
                      {!isCollapsed && (
                        <>
                          <span className="nav-link-title flex-grow-1">{sidebar.title}</span>
                          <IconChevronDown
                            size={14}
                            style={{
                              transition: "transform 0.2s",
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              opacity: 0.6,
                            }}
                          />
                        </>
                      )}
                    </a>
                    {!isCollapsed && isExpanded && (
                      <ul className="nav flex-column" style={{ paddingLeft: "2.5rem", marginTop: "2px" }}>
                        {sidebar.children.map((child, childIndex) => {
                          const isChildActive = location.pathname === child.href;
                          return (
                            <li key={childIndex} className={`nav-item ${isChildActive ? "active" : ""}`}>
                              <a
                                className={`nav-link py-1 ${isChildActive ? "active" : ""}`}
                                href="#"
                                style={{ fontSize: "13px" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  redirect(child.href);
                                }}
                              >
                                {child.title}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              // Regular item
              const Icon = sidebar.icon;
              const isActive = location.pathname === sidebar.href;

              const navLink = (
                <a
                  className={`nav-link d-flex align-items-center ${isActive ? "active" : ""}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setExpandedMenu(null);
                    redirect(sidebar.href);
                  }}
                >
                  <span className="nav-link-icon d-inline-flex align-items-center justify-content-center">
                    <Icon size={18} />
                  </span>
                  {!isCollapsed && <span className="nav-link-title">{sidebar.title}</span>}
                </a>
              );

              return (
                <li key={index} className={`nav-item ${isActive ? "active" : ""}`} title={isCollapsed ? sidebar.title : undefined}>
                  {navLink}
                </li>
              );
            })}



            {/* Logout */}
            <li className="nav-item mt-auto">
              {(() => {
                const logoutLink = (
                  <a
                    className="nav-link d-flex align-items-center text-danger"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsConfirmationDialog(true);
                    }}
                  >
                    <span className="nav-link-icon d-inline-flex align-items-center justify-content-center text-danger">
                      <IconLogout size={18} />
                    </span>
                    {!isCollapsed && <span className="nav-link-title text-danger">{sidebarTitles.logout}</span>}
                  </a>
                );

                return isCollapsed ? (
                  <span title={sidebarTitles.logout}>
                    {logoutLink}
                  </span>
                ) : (
                  logoutLink
                );
              })()}
            </li>
          </ul>

          {/* Footer / Switch Branch */}
          {!isServiceAdmin && (
            <div className="navbar-footer border-top border-secondary-subtle p-3 w-100 mt-auto d-flex justify-content-center">
              {isCollapsed ? (
                <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      redirect(PathConstant.BRANCH_MANAGEMENT);
                    }}
                    className="btn btn-outline-light btn-icon"
                    style={{ width: "40px", height: "40px", borderRadius: "8px" }}
                    title={getLabel(LangConstant.L_SWITCH_BRANCH)}
                  >
                    <IconRefresh size={16} />
                  </a>
              ) : (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    redirect(PathConstant.BRANCH_MANAGEMENT);
                  }}
                  className="btn btn-outline-light w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{ borderRadius: "8px" }}
                >
                  <IconRefresh size={16} />
                  <span>{getLabel(LangConstant.L_SWITCH_BRANCH)}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <AdvancedConfirmDialog
        open={isConfirmationDialog}
        title={getLabel("TXT_LOGOUT_TITLE")}
        content={getLabel("TXT_LOGOUT_TEXT")}
        submitProps={{
          submitText: getLabel("TXT_LOGOUT_BUTTON"),
          onClick: AuthUtils.handleLogout,
        }}
        cancelProps={{
          onClick: handleCloseConfirmationDialog,
        }}
      />
    </aside>
  );
};

export default memo(Sidebar);
